import { Dropdown, DropdownButton } from 'react-bootstrap';
import { useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import { selectDoctor } from 'redux/features/auth/authSlice';
import { selectProntuarioById, storeProntuario, updateProntuario } from 'services/prontuario';
import styles from 'styles/styles.module.css';
import { useSWRConfig } from 'swr';

export default function PopupMenu({
  prontuario, // Consulta referente a este card/item específico
  setProntuario,
  prontuarios,
  setProntuarios,
  onClick,
  isDisabled = false,
  onSolicitarCopia,
}) {
  const { mutate } = useSWRConfig();
  const medico = useSelector(selectDoctor);

  // Função para copiar dados da consulta deste card para a consulta ativa na tela
  const handleCopiarParaAtual = async () => {
    const consultaHoje = prontuarios?.[0];

    if (!consultaHoje) {
      toast.warn('Nenhuma consulta de hoje encontrada para receber os dados.');
      return;
    }

    if (String(prontuario.CODCONSULTA) === String(consultaHoje.CODCONSULTA)) {
      toast.info('Esta é a própria consulta de hoje.');
      return;
    }

    if (
      ['PCP', 'SEC', 'ORT'].includes(consultaHoje?.TIPO?.trim()) &&
      consultaHoje?.GRAVADO?.trim() === 'SIM'
    ) {
      toast.warn('A consulta atual já foi gravada definitivamente e não pode ser alterada.');
      return;
    }

    let dadosOrigem = prontuario;
    if (
      dadosOrigem.CODCONSULTA &&
      dadosOrigem.QUEIXAS === undefined &&
      dadosOrigem.MEDICAMENTOS === undefined
    ) {
      try {
        dadosOrigem = await selectProntuarioById(dadosOrigem.CODCONSULTA);
      } catch (err) {
        console.error('Erro ao buscar dados completos da consulta de origem:', err);
      }
    }

    const camposClinicos = [
      'ANTECEDENTES_FLIA',
      'ANTECEDENTES_PESSOAIS',
      'CID',
      'CID2',
      'CID3',
      'CONDUTA',
      'DESCRICAO',
      'EXAMES_COMPLEMENTARES',
      'EXAME_FISICO',
      'HABITOS',
      'HIPOTESE_DIAGNOSTICA',
      'ISDA',
      'MEDICAMENTOS',
      'OBS',
      'OBSERVACOES',
      'QUEIXAS',
      'RECEITA_GERAL',
      'RECEITA_MEDICTO',
      'TIPOATESTADO',
      'TIPORECEITA',
    ];

    const dadosCopiados = {};
    camposClinicos.forEach((campo) => {
      if (dadosOrigem[campo] !== undefined) {
        dadosCopiados[campo] = dadosOrigem[campo];
      }
    });

    const prontuarioAtualizado = {
      ...consultaHoje,
      ...dadosCopiados,
      GRAVADO: 'PAR',
      TIPO: 'SEC',
    };

    // Dispara a exibição do Alert no componente pai
    onSolicitarCopia({
      consultaHoje,
      prontuarioAtualizado,
      dataOrigem: dadosOrigem.dataFormatada || prontuario.dataFormatada || 'data selecionada',
    });
  };

  const handleConsultaCardiologista = async (tipo) => {
    // Bloqueia edição se a consulta já estiver gravada definitivamente
    if (
      ['PCP', 'SEC'].includes(prontuario?.TIPO?.trim()) &&
      prontuario?.GRAVADO?.trim() === 'SIM'
    ) {
      toast.warn('Esta consulta já foi gravada definitivamente e não pode mais ser alterada.');
      return;
    }
    try {
      // 1) Verifica se já existe PCP
      const existePCP = prontuarios?.some((p) => p?.TIPO?.trim() === 'PCP');
      // 2) Se for SEC e não houver PCP, alerta e bloqueia
      if (tipo === 'SEC' && !existePCP) {
        toast.warn(
          'É necessário registrar uma Consulta Principal (PCP) antes de adicionar uma Consulta Complementar (SEC)!'
        );
        return;
      }

      let prontuarioAtualizado = { ...prontuario, TIPO: tipo };

      // 3) Se for PCP e já existe PCP, alerta e bloqueia múltiplos PCP
      if (tipo === 'PCP' && existePCP) {
        toast.warn(
          'Já existe uma Consulta Principal (PCP) registrada para este paciente. Apenas uma PCP é permitida.'
        );
        return;
      }

      // 4) Se for SEC, copia dados da última PCP ou SEC
      if (tipo === 'SEC') {
        // Busca a última consulta PCP ou SEC (com GRAVADO = 'SIM') anterior à atual
        const consultasValidas = prontuarios
          ?.filter(
            (p) =>
              p.CODCONSULTA !== prontuario.CODCONSULTA &&
              ['PCP', 'SEC'].includes(p?.TIPO?.trim()) &&
              p?.GRAVADO?.trim() === 'SIM'
          )
          .sort(
            (a, b) =>
              new Date(b.DATACONSULTA || b.createdAt) - new Date(a.DATACONSULTA || a.createdAt)
          );
        let ultimaConsulta = consultasValidas?.[0];

        if (
          ultimaConsulta &&
          ultimaConsulta.CODCONSULTA &&
          ultimaConsulta.QUEIXAS === undefined &&
          ultimaConsulta.MEDICAMENTOS === undefined
        ) {
          try {
            ultimaConsulta = await selectProntuarioById(ultimaConsulta.CODCONSULTA);
          } catch (err) {
            console.error('Erro ao buscar dados completos da consulta anterior:', err);
          }
        }

        if (ultimaConsulta) {
          // Copia apenas campos clínicos relevantes, sem sobrescrever identificadores da nova consulta
          const camposClinicos = [
            'ANTECEDENTES_FLIA',
            'ANTECEDENTES_PESSOAIS',
            'CID',
            'CID2',
            'CID3',
            'CONDUTA',
            'DESCRICAO',
            'EXAMES_COMPLEMENTARES',
            'EXAME_FISICO',
            'HABITOS',
            'HIPOTESE_DIAGNOSTICA',
            'ISDA',
            'MEDICAMENTOS',
            'OBS',
            'OBSERVACOES',
            'QUEIXAS',
            'RECEITA_GERAL',
            'RECEITA_MEDICTO',
            'TIPOATESTADO',
            'TIPORECEITA',
          ];
          const dadosCopiados = {};
          camposClinicos.forEach((campo) => {
            if (ultimaConsulta[campo] !== undefined) {
              dadosCopiados[campo] = ultimaConsulta[campo];
            }
          });

          prontuarioAtualizado = {
            ...prontuarioAtualizado,
            ...dadosCopiados,
            TIPO: 'SEC',
            GRAVADO: 'PAR', // nova consulta começa como parcial
          };
        }
      }

      // 5) PCP sempre começa como GRAVADO: 'PAR'
      if (tipo === 'PCP') {
        prontuarioAtualizado.GRAVADO = 'PAR';
      }

      // Atualiza o cache do SWR de forma otimista
      if (typeof setProntuarios === 'function') {
        await setProntuarios(
          (prev = []) =>
            Array.isArray(prev)
              ? prev.map((p) =>
                  p.CODCONSULTA === prontuario.CODCONSULTA ? { ...p, ...prontuarioAtualizado } : p
                )
              : prev,
          { revalidate: false }
        );
      }

      await updateProntuario(prontuario.CODCONSULTA, prontuarioAtualizado);

      // Atualiza o estado da consulta e reseta os campos do formulário
      setProntuario(prontuarioAtualizado);

      // Revalida a lista de prontuários com o backend
      if (typeof setProntuarios === 'function') {
        await setProntuarios();
      } else {
        await mutate((key) => Array.isArray(key) && key[0] === 'prontuarios');
      }

      toast.success(
        `Prontuário alterado para ${tipo === 'PCP' ? 'Consulta Principal' : 'Consulta de Acompanhamento'}`
      );
    } catch (err) {
      console.error('Erro ao atualizar tipo do prontuário:', err);
      const message = err?.response?.data?.message || 'Erro ao atualizar prontuário';
      toast.error(message);
      setProntuario(prontuario);
    }
  };

  const handleConsultaOrtopedista = async (tipo) => {
    // Apenas o médico com codMed=69 pode realizar
    if (medico?.codMed !== 69 && medico?.codMed !== 7) {
      toast.warn('Somente o ortopedista pode registrar este tipo de consulta.');
      return;
    }

    // Bloqueia edição se a consulta já estiver gravada definitivamente
    if (prontuario?.TIPO?.trim() === 'ORT' && prontuario?.GRAVADO?.trim() === 'SIM') {
      toast.warn('Esta consulta já foi gravada definitivamente e não pode mais ser alterada.');
      return;
    }

    try {
      // Busca a última consulta do tipo ORT (com GRAVADO = 'SIM') anterior à atual
      const consultasValidas = prontuarios
        ?.filter(
          (p) =>
            p.CODCONSULTA !== prontuario.CODCONSULTA &&
            p?.TIPO?.trim() === 'ORT' &&
            p?.GRAVADO?.trim() === 'SIM'
        )
        .sort(
          (a, b) =>
            new Date(b.DATACONSULTA || b.createdAt) - new Date(a.DATACONSULTA || a.createdAt)
        );
      let ultimaConsulta = consultasValidas?.[0];

      if (
        ultimaConsulta &&
        ultimaConsulta.CODCONSULTA &&
        ultimaConsulta.QUEIXAS === undefined &&
        ultimaConsulta.MEDICAMENTOS === undefined
      ) {
        try {
          ultimaConsulta = await selectProntuarioById(ultimaConsulta.CODCONSULTA);
        } catch (err) {
          console.error('Erro ao buscar dados completos da consulta ortopedista anterior:', err);
        }
      }

      let prontuarioAtualizado = { ...prontuario, TIPO: 'ORT' };
      if (ultimaConsulta) {
        // Copia apenas campos clínicos relevantes, sem sobrescrever identificadores da nova consulta
        const camposClinicos = [
          'ANTECEDENTES_FLIA',
          'ANTECEDENTES_PESSOAIS',
          'CID',
          'CID2',
          'CID3',
          'CONDUTA',
          'DESCRICAO',
          'EXAMES_COMPLEMENTARES',
          'EXAME_FISICO',
          'HABITOS',
          'HIPOTESE_DIAGNOSTICA',
          'ISDA',
          'MEDICAMENTOS',
          'OBS',
          'OBSERVACOES',
          'QUEIXAS',
          'RECEITA_GERAL',
          'RECEITA_MEDICTO',
          'TIPOATESTADO',
          'TIPORECEITA',
        ];
        const dadosCopiados = {};
        camposClinicos.forEach((campo) => {
          if (ultimaConsulta[campo] !== undefined) {
            dadosCopiados[campo] = ultimaConsulta[campo];
          }
        });
        prontuarioAtualizado = {
          ...prontuarioAtualizado,
          ...dadosCopiados,
          TIPO: 'ORT',
          GRAVADO: 'PAR', // nova consulta começa como parcial
        };
      } else {
        prontuarioAtualizado.GRAVADO = 'PAR';
      }

      // Atualiza o cache do SWR de forma otimista
      if (typeof setProntuarios === 'function') {
        await setProntuarios(
          (prev = []) =>
            Array.isArray(prev)
              ? prev.map((p) =>
                  p.CODCONSULTA === prontuario.CODCONSULTA ? { ...p, ...prontuarioAtualizado } : p
                )
              : prev,
          { revalidate: false }
        );
      }

      // Faz requisição ao servidor
      await updateProntuario(prontuario.CODCONSULTA, prontuarioAtualizado);

      // Atualiza o estado da consulta e reseta os campos do formulário
      setProntuario(prontuarioAtualizado);

      // Revalidar a lista de prontuários
      if (typeof setProntuarios === 'function') {
        await setProntuarios();
      } else {
        await mutate((key) => Array.isArray(key) && key[0] === 'prontuarios');
      }

      toast.success('Prontuário alterado para Consulta Ortopedista');
    } catch (err) {
      console.error('Erro ao atualizar tipo do prontuário:', err);
      const message = err?.response?.data?.message || 'Erro ao atualizar prontuário';
      toast.error(message);
      setProntuario(prontuario);
    }
  };

  const handleConsultaTelefone = async (tipo) => {
    const isOrtopedista = medico?.codMed === 69;
    const isCardio = medico?.codMed !== 69;
    if (!isOrtopedista && !isCardio) {
      toast.warn('Somente ortopedista ou cardiologista pode registrar consulta por telefone.');
      return;
    }

    try {
      const consultasValidas = prontuarios
        ?.filter(
          (p) => ['ORT', 'PCP', 'SEC'].includes(p?.TIPO?.trim()) && p?.GRAVADO?.trim() === 'SIM'
        )
        .sort((a, b) => new Date(b.dataFormatada) - new Date(a.dataFormatada));
      const ultimaConsulta = consultasValidas?.[0];
      if (!ultimaConsulta) {
        toast.warn('Não há consulta anterior válida para copiar.');
        return;
      }

      // Gera nova consulta com dados da anterior e codprocedi=800
      const camposClinicos = [
        'ANTECEDENTES_FLIA',
        'ANTECEDENTES_PESSOAIS',
        'CID',
        'CID2',
        'CID3',
        'CONDUTA',
        'DESCRICAO',
        'EXAMES_COMPLEMENTARES',
        'EXAME_FISICO',
        'HABITOS',
        'HIPOTESE_DIAGNOSTICA',
        'ISDA',
        'MEDICAMENTOS',
        'OBS',
        'OBSERVACOES',
        'QUEIXAS',
        'RECEITA_GERAL',
        'RECEITA_MEDICTO',
        'TIPOATESTADO',
        'TIPORECEITA',
      ];
      const dadosCopiados = {};
      camposClinicos.forEach((campo) => {
        if (ultimaConsulta[campo] !== undefined) {
          dadosCopiados[campo] = ultimaConsulta[campo];
        }
      });

      const novaConsulta = {
        ...dadosCopiados,
        TIPO: 'SEC',
        GRAVADO: 'PAR',
        CODPROCEDI: 800,
        CODPAC: prontuario.CODPAC,
        CODMEDICO: prontuario.CODMEDICO,
        CODREALIZOU: prontuario.CODREALIZOU,
        CODENCAMINHOU: prontuario.CODENCAMINHOU,
        CODCONV: prontuario.CODCONV,
        CODFUNC: prontuario.CODFUNC,
        CODFUNCLAUDO: prontuario.CODFUNCLAUDO,
        DATACONSULTA: new Date().toISOString().split('T')[0], // data atual
      };

      const res = await storeProntuario(novaConsulta);
      const id = res?.CODCONSULTA || res?.data?.CODCONSULTA || res?.id;
      if (id) {
        const consultaFormatada = await selectProntuarioById(id);
        setProntuarios?.((prevProntuarios) => [consultaFormatada, ...prevProntuarios]);
        setProntuario(consultaFormatada);
      } else {
        mutate((key) => Array.isArray(key) && key[0] === 'prontuarios');
      }
      toast.success('Nova consulta por telefone criada com sucesso.');
    } catch (err) {
      const message = err?.response?.data?.message || 'Erro ao criar consulta por telefone.';
      toast.error(message);
    }
  };

  return (
    <DropdownButton
      id={`dropdown-prontuario-${prontuario.CODCONSULTA}`}
      title=""
      variant="outline-secondary"
      size="sm"
      drop="down"
      onClick={onClick}
      style={{ padding: '1px 5px 1px 1px' }}
      className="popupmenu-dropdown"
    >
      <Dropdown.Header
        className="text-center fw-bold"
        style={{
          fontWeight: 'bold',
          fontSize: '1.02rem',
          letterSpacing: '0.5px',
          padding: '1px 10px 10px 10px',
        }}
      >
        CONSULTAS
      </Dropdown.Header>
      <Dropdown.Item
        as="button"
        disabled={isDisabled}
        onClick={() => handleConsultaCardiologista('PCP')}
        className="fw-bold text-primary text-truncate"
        style={{
          padding: '1px 10px 10px 10px',
          fontWeight: 'bold',
          fontSize: '0.98rem',
          minWidth: 180,
          maxWidth: 260,
        }}
        title="Consulta Principal"
      >
        📋 Consulta Principal
      </Dropdown.Item>
      <Dropdown.Item
        as="button"
        disabled={isDisabled}
        onClick={() => handleConsultaCardiologista('SEC')}
        className="fw-bold text-danger text-truncate"
        style={{
          padding: '1px 10px 10px 10px',
          fontWeight: 'bold',
          fontSize: '0.98rem',
          minWidth: 180,
          maxWidth: 260,
        }}
        title="Consulta de Acompanhamento"
      >
        📝 Acompanhamento
      </Dropdown.Item>
      <Dropdown.Item
        as="button"
        disabled={isDisabled}
        onClick={() => handleConsultaOrtopedista('ORT')}
        className={`fw-bold ${styles.textPurple} text-truncate`}
        style={{
          padding: '1px 10px 10px 10px',
          fontWeight: 'bold',
          fontSize: '0.98rem',
          minWidth: 180,
          maxWidth: 260,
        }}
        title="Consulta Ortopedista"
      >
        📝 Ortopedista
      </Dropdown.Item>
      <Dropdown.Item
        as="button"
        disabled={isDisabled}
        onClick={() => handleConsultaTelefone('SEC')}
        className="fw-bold text-warning text-truncate"
        style={{
          padding: '1px 10px 10px 10px',
          fontWeight: 'bold',
          fontSize: '0.98rem',
          minWidth: 180,
          maxWidth: 260,
        }}
        title="Consulta Telefone"
      >
        📝 Consulta Telefone
      </Dropdown.Item>
      <Dropdown.Divider />
      <Dropdown.Header
        className="text-center fw-bold"
        style={{
          fontWeight: 'bold',
          fontSize: '1.02rem',
          letterSpacing: '0.5px',
          padding: '1px 10px 10px 10px',
        }}
      >
        AÇÕES
      </Dropdown.Header>

      <Dropdown.Item
        as="button"
        disabled={isDisabled}
        onClick={handleCopiarParaAtual}
        className="fw-bold text-success text-truncate"
        style={{
          padding: '1px 10px 10px 10px',
          fontWeight: 'bold',
          fontSize: '0.98rem',
          minWidth: 180,
          maxWidth: 260,
        }}
        title="Copiar dados desta consulta para a consulta atual"
      >
        📋 Copiar para Consulta Atual
      </Dropdown.Item>
    </DropdownButton>
  );
}
